import React, { useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import {
  ChevronLeft,
  Search,
  Plus,
  Eye,
  MoreVertical,
  FileText,
  Edit,
  User,
  CornerDownRight,
} from "lucide-react";
import { CLOSED_EYE, RED_BIN } from "../../../../../assets";
import RenameModal from "./modals/RenameModal";
import DeletePromptModal from "./modals/WarningPromptModal";
import DeleteSuccessModal from "./modals/DeleteSuccessModal";
import DocumentDetailsModal from "./modals/DocumentDetailsModal";
import {
  selectJoinAs,
  selectThirdPartyType,
  setThirdPartyType,
  ThirdPartyType,
} from "../../../../../states/slices/globalReducer";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import ModalV2 from "../../../../../shared/components/modalV2";
import WarningPromptModal from "./modals/WarningPromptModal";

// Mock Data for Queries
const MOCK_QUERIES = [
  {
    id: 1,
    user: { name: "Legal Officer", role: "Lawyer" },
    content:
      "What is the required notice period for termination without cause as stipulated in Section 5.2?",
    time: "2hr",
    replies: [
      {
        id: 11,
        user: { name: "Legal Officer", role: "Lawyer" },
        content:
          "The required notice period for termination without cause is 30 days, as per the employment agreement standards.",
        time: "2hr",
      },
    ],
  },
  {
    id: 2,
    user: { name: "Legal Officer", role: "Lawyer" },
    content:
      "What is the required notice period for termination without cause as stipulated in Section 5.2",
    time: null,
    isActive: true,
  },
];

// Mock Data for Documents
const DOCUMENTS = [
  {
    id: 1,
    name: "companies_demo_export.xlsx",
    date: "2021-11-04 11:54",
    type: "xlsx",
    color: "bg-[#00B074]", // Green
  },
  {
    id: 2,
    name: "demo_image.jpg",
    date: "2021-11-03 22:00",
    type: "jpg",
    color: "bg-[#7F56D9]", // Purple
  },
  {
    id: 3,
    name: "sample_demo_export.xlsx",
    date: "2021-11-02 11:09",
    type: "xlsx",
    color: "bg-[#7F56D9]", // Purple
  },
  {
    id: 4,
    name: "visit_demo_export.xlsx",
    date: "2021-10-31 17:24",
    type: "xlsx",
    color: "bg-[#7F56D9]", // Purple
  },
];

const FileDefenceProcess = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"queries" | "activity_logs">(
    "queries",
  );
  const dispatch = useAppDispatch();

  const selectedThirdPartyType_ = useAppSelector(selectThirdPartyType);
  const [detailsModal, setDetailsModal] = useState(false);
  const [renameModal, setRenameModal] = useState(false);
  const [deletePromptModal, setDeletePromptModal] = useState(false);
  const [deleteSuccessModal, setDeleteSuccessModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const selectedJoinedAs = useAppSelector(selectJoinAs);
  const [thirdPartyTypesModal, setThirdPartyTypesModal] = useState(false);

  return (
    <div className="flex flex-col w-full h-full overflow-y-auto  min-h-screen">
      {/* Header & Breadcrumbs */}
      <div className="bg-white border-b border-[#E9EAEB] px-8 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-x-2 text-sm text-[#475467] mb-6">
          <button
            onClick={() => navigate("/police/case-folders")}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span
            onClick={() => navigate("/police/case-folders")}
            className="cursor-pointer hover:text-[#1A1A1A] transition-colors"
          >
            Case folders
          </span>
          <span className="text-[#D0D5DD]">{">"}</span>
          <span className="font-medium text-[#1A1A1A]">
            Olugbenga Akintola vs Israel Anyamelu
          </span>
        </div>

        <div className="flex items-center justify-between">
          <h1 className="text-[28px] font-bold text-[#1A1A1A]">
            Olugbenga Akintola vs Israel Anyamelu
          </h1>
          {selectedJoinedAs === "DEFENDAANT" ? (
            <button
              onClick={() =>
                navigate("/police/case-folders/file-defendant-process")
              }
              className="flex items-center gap-x-2  px-4 py-2.5 rounded-lg text-sm font-medium text-white hover:bg-opacity-65 transition-colors bg-[#00B074] shadow-sm"
            >
              <span>File Defence Process</span>
            </button>
          ) : (
            <button
              onClick={() =>
                navigate("/police/case-folders/file-third-party-process")
              }
              className="flex items-center gap-x-2  px-4 py-2.5 rounded-lg text-sm font-medium text-white hover:bg-opacity-65 transition-colors bg-[#00B074] shadow-sm"
            >
              <span>Join Case</span>
            </button>
          )}
        </div>
      </div>

      <div className="px-8 py-8 flex flex-col gap-y-8">
        {/* Search Bar */}
        <div className="w-full max-w-[264px] relative">
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-[#D0D5DD] rounded-lg text-sm placeholder:text-[#667085] focus:outline-none focus:border-[#00B074] focus:ring-1 focus:ring-[#00B074] shadow-sm"
          />
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#667085]" />
        </div>

        {/* Documents List */}
        <div className="flex flex-col gap-y-3">
          {DOCUMENTS.map((doc) => (
            <div
              key={doc.id}
              className="bg-white border border-[#E9EAEB] rounded-xl p-4 flex items-center justify-between hover:shadow-sm transition-shadow"
            >
              <div className="flex items-center gap-x-4 flex-1">
                {/* File Icon */}
                <div
                  className={`w-10 h-10 rounded-full ${doc.color} flex items-center justify-center text-white font-medium text-sm`}
                >
                  {doc.name.charAt(0).toUpperCase()}
                </div>

                {/* File Details */}
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="text-sm font-medium text-[#101828] truncate">
                    {doc.name}
                  </span>
                </div>

                {/* Date */}
                <div className="text-sm text-[#475467] w-40">{doc.date}</div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-x-4">
                <button className="flex items-center gap-x-2 text-[#027A48] hover:text-[#026aa2] px-3 py-2 rounded-lg transition-colors">
                  <img src={CLOSED_EYE} className="w-[24px] h-[24px]" alt="" />
                  <span className="text-sm font-medium">View</span>
                </button>

                <button className="flex items-center gap-x-2 bg-[#ECFDF3] text-[#027A48] px-4 py-2 rounded-full text-sm font-medium hover:bg-[#d1fadf] transition-colors">
                  Download
                </button>

                <button
                  onClick={() => setDeletePromptModal(true)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-[#FEF3F2] text-[#B42318] hover:bg-[#fee4e2] transition-colors"
                >
                  <img src={RED_BIN} className="w-[11px] h-[11px]" alt="" />
                </button>

                <Menu as="div" className="relative inline-block text-left">
                  <Menu.Button className="p-2 text-[#98A2B3] hover:text-[#475467] transition-colors focus:outline-none">
                    <MoreVertical className="w-5 h-5" />
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right divide-y divide-gray-100 rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 py-2">
                      <div className="px-1 py-1 flex flex-col gap-y-1">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              className={`${
                                active ? "bg-gray-50" : ""
                              } group flex w-full items-center gap-x-3 rounded-lg px-4 py-2.5 text-sm font-medium text-[#344054]`}
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => setRenameModal(true)}
                              className={`${
                                active ? "bg-gray-50" : ""
                              } group flex w-full items-center gap-x-3 rounded-lg px-4 py-2.5 text-sm font-medium text-[#344054]`}
                            >
                              <Edit className="w-4 h-4" />
                              Rename
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => setDetailsModal(true)}
                              className={`${
                                active
                                  ? "bg-[#E6F7F1] text-[#00B074]"
                                  : "text-[#344054]"
                              } group flex w-full items-center gap-x-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors`}
                            >
                              <FileText className="w-4 h-4" />
                              Details
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex flex-col gap-y-6 mt-4">
          <div className="border-b border-[#E9EAEB] flex items-center gap-x-8">
            <button
              onClick={() => setActiveTab("queries")}
              className={`pb-4 text-sm font-medium transition-colors relative ${
                activeTab === "queries"
                  ? "text-[#00B074]"
                  : "text-[#667085] hover:text-[#344054]"
              }`}
            >
              Queries (2)
              {activeTab === "queries" && (
                <div className="absolute bottom-0 left-0 w-full h-[2px] bg-[#00B074] rounded-t-sm" />
              )}
            </button>
            <button
              onClick={() => setActiveTab("activity_logs")}
              className={`pb-4 text-sm font-medium transition-colors relative ${
                activeTab === "activity_logs"
                  ? "text-[#00B074]"
                  : "text-[#667085] hover:text-[#344054]"
              }`}
            >
              Activity logs
              {activeTab === "activity_logs" && (
                <div className="absolute bottom-0 left-0 w-full h-[2px] bg-[#00B074] rounded-t-sm" />
              )}
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === "queries" && (
            <div className="flex flex-col gap-y-8">
              <div className="flex justify-end">
                <button className="flex items-center gap-x-2 bg-[#00B074] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#009b65] shadow-sm transition-colors">
                  <Plus className="w-4 h-4" />
                  <span>Make new queries</span>
                </button>
              </div>

              <div className="flex flex-col gap-y-6">
                {MOCK_QUERIES.map((query) => (
                  <div key={query.id} className="flex flex-col">
                    {/* Parent Query */}
                    <div className="flex gap-x-4 relative">
                      {/* Avatar Column */}
                      <div className="flex flex-col items-center">
                        <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center text-white shrink-0 z-10">
                          <User className="w-5 h-5" />
                        </div>
                        {/* Connecting Line */}
                        {(query.replies?.length! > 0 || query.isActive) && (
                          <div className="w-[2px] bg-[#E9EAEB] h-full absolute top-10 left-5 -translate-x-1/2" />
                        )}
                      </div>

                      {/* Content Column */}
                      <div className="flex flex-col gap-y-1 w-full pb-2">
                        <div className="flex flex-col">
                          <span className="font-bold text-[#101828] text-sm">
                            {query.user.name}
                          </span>
                          <span className="text-xs text-[#475467]">
                            {query.user.role}
                          </span>
                        </div>
                        <p className="text-[#101828] font-bold text-base mt-1">
                          {query.content}
                        </p>

                        {!query.isActive && (
                          <div className="flex items-center gap-x-4 mt-2">
                            <button className="flex items-center gap-x-2 text-[#475467] text-sm font-medium hover:text-[#1A1A1A]">
                              <CornerDownRight className="w-4 h-4" />
                              Reply
                            </button>
                            {query.time && (
                              <span className="text-[#475467] text-sm">
                                {query.time}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Replies */}
                    {query.replies?.map((reply) => (
                      <div
                        key={reply.id}
                        className="flex gap-x-4 relative pt-6"
                      >
                        {/* Avatar Column */}
                        <div className="flex flex-col items-center">
                          {/* Connecting Line from top */}
                          <div className="w-[2px] bg-[#E9EAEB] h-6 absolute top-0 left-5 -translate-x-1/2" />

                          <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center text-white shrink-0 z-10">
                            <User className="w-5 h-5" />
                          </div>
                        </div>

                        {/* Content Column */}
                        <div className="flex flex-col gap-y-1 w-full">
                          <div className="flex flex-col">
                            <span className="font-bold text-[#101828] text-sm">
                              {reply.user.name}
                            </span>
                            <span className="text-xs text-[#475467]">
                              {reply.user.role}
                            </span>
                          </div>
                          <p className="text-[#101828] font-medium text-sm mt-1">
                            {reply.content}
                          </p>

                          <div className="flex items-center gap-x-4 mt-2">
                            <button className="flex items-center gap-x-2 text-[#475467] text-sm font-medium hover:text-[#1A1A1A]">
                              <CornerDownRight className="w-4 h-4" />
                              Reply
                            </button>
                            {reply.time && (
                              <span className="text-[#475467] text-sm">
                                {reply.time}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Active Input Section */}
                    {query.isActive && (
                      <div className="flex gap-x-4 pt-6 relative">
                        {/* Empty Avatar Placeholder to align indent */}
                        <div className="w-10 shrink-0 relative">
                          {/* Connecting Line from top */}
                          <div className="w-[2px] bg-[#E9EAEB] h-6 absolute top-0 left-5 -translate-x-1/2" />
                        </div>

                        <div className="flex flex-col w-full gap-y-4">
                          <input
                            type="text"
                            placeholder="Reply to query"
                            className="w-full border border-[#D0D5DD] rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#00B074] focus:ring-1 focus:ring-[#00B074] shadow-sm"
                          />
                          <div className="flex items-center gap-x-3">
                            <button className="bg-[#00B074] text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-[#009b65] shadow-sm transition-colors">
                              Reply
                            </button>
                            <button className="bg-white border border-[#00B074] text-[#00B074] px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#E6F7F1] transition-colors">
                              Resolve Query
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "activity_logs" && (
            <div className="flex flex-col items-center justify-center py-12 text-[#667085]">
              <p>Activity logs will appear here</p>
            </div>
          )}

          <RenameModal
            isOpen={renameModal}
            onClose={() => setRenameModal(false)}
            onClick={() => {}}
            name=""
            setName={(e) => {}}
          />
          <WarningPromptModal
            isOpen={deletePromptModal}
            onClose={() => setDeletePromptModal(false)}
            onClick={() => {
              setDeleteSuccessModal(true);
              setDeletePromptModal(false);
            }}
          />
          <DeleteSuccessModal
            open={deleteSuccessModal}
            close={() => setDeleteSuccessModal(false)}
            message="Document deleted"
          />
          <DocumentDetailsModal
            open={detailsModal}
            close={() => setDetailsModal(false)}
          />
          <ModalV2
            isOpen={thirdPartyTypesModal}
            isClose={() => setThirdPartyTypesModal(false)}
            maxWidth="w-[442px]"
            edges="rounded-xl"
          >
            <div className="px-8 py-10 flex flex-col items-start w-full gap-y-5">
              <span className="text-2xl font-semibold">
                Select who you are joining as
              </span>
              <div className="flex flex-col w-full gap-y-3">
                {[
                  { value: "DEFENDANT", label: "Defendant" },
                  { value: "PLAINTIFF", label: "Plaintiff" },
                ].map((type) => (
                  <div className="flex items-center gap-x-3">
                    <div
                      onClick={() =>
                        dispatch(
                          setThirdPartyType(type.value as ThirdPartyType),
                        )
                      }
                      className="w-[18px] h-[18px] flex items-center justify-center rounded-full border border-[#23A863]"
                    >
                      {selectedThirdPartyType_ === type.value && (
                        <div className="w-[10px] h-[10px] rounded-full bg-[#23A863]" />
                      )}
                    </div>
                    <span className="text-[16px] font-medium">
                      {type.label}
                    </span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => {
                  setThirdPartyTypesModal(false);
                  navigate("/police/case-folders/file-third-party-process");
                }}
                className="bg-[#009444] w-full text-white px-10 py-3 rounded-xl"
              >
                Proceed
              </button>
            </div>
          </ModalV2>
        </div>
      </div>
    </div>
  );
};

export default FileDefenceProcess;
